// ===========================================
// The school's colour for each login role (#629)
// ===========================================
// Settings → Colors → User Role Colors: the ONE source for an Admin (shown as the owner title),
// Teacher, Parent or Student colour. Avatars, the "view as" chips, the Roles page access chip and
// the enquiry owner chips all read it here; before #629 each used its own hard-coded table (two of
// which disagreed) and the setting was read by nothing. Any signed-in user may read it. While
// loading, or if the read fails, the brand defaults stand: a colour must never block a page.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as adminSettingsApi from '../api/adminSettings.api';
import { ROLE_AVATAR_COLORS, type UserRole } from '../types/avatar.types';
import type { RoleColorEntry, RoleColorsSettings } from '../types/adminSettings.types';

const HEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * `adminSettingsKeys.roleColors()`, spelled out so this hook does not import `useAdminSettings`
 * (avatars render everywhere, and many tests mock that module whole). Pinned equal by a test, so a
 * save in Settings → Colors still refreshes every chip and avatar.
 */
export const ROLE_COLORS_QUERY_KEY = ['admin-settings', 'role-colors'] as const;

/** A stored entry only when both colours are well formed; otherwise the default for that role. */
function entryOrDefault(stored: Partial<RoleColorEntry> | undefined, role: UserRole): RoleColorEntry {
  return stored && HEX.test(stored.background ?? '') && HEX.test(stored.text ?? '')
    ? { background: stored.background!, text: stored.text! }
    : ROLE_AVATAR_COLORS[role];
}

export function resolveRoleColors(stored: Partial<RoleColorsSettings> | undefined): RoleColorsSettings {
  return {
    ADMIN: entryOrDefault(stored?.ADMIN, 'ADMIN'),
    TEACHER: entryOrDefault(stored?.TEACHER, 'TEACHER'),
    PARENT: entryOrDefault(stored?.PARENT, 'PARENT'),
    STUDENT: entryOrDefault(stored?.STUDENT, 'STUDENT'),
  };
}

/** Every role's colour, never undefined (the defaults while loading or on an error). */
export function useRoleColorScheme(): RoleColorsSettings {
  const { data } = useQuery({
    // The same key as Settings → Colors, so a save there recolours every chip and avatar.
    queryKey: ROLE_COLORS_QUERY_KEY,
    // Looked up inside the query, not at render: any failure to read is a failed query (defaults).
    queryFn: () => adminSettingsApi.getRoleColors(),
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => resolveRoleColors(data), [data]);
}
